'use client';
import { ApolloLink, HttpLink } from '@apollo/client';

import { getMainDefinition } from '@apollo/client/utilities';

import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';

import {
  ApolloNextAppProvider,
  ApolloClient,
  InMemoryCache,
  SSRMultipartLink,
} from '@apollo/client-integration-nextjs';

import { loadErrorMessages, loadDevMessages } from '@apollo/client/dev';
import { RetryLink } from '@apollo/client/link/retry';
import { setVerbosity } from 'ts-invariant';

import { env } from '@/env';

import { getRestApiUrl } from '@/utils/getRestApiUrl';

import { typePolicies } from './typePolicies';

if (env.NEXT_PUBLIC_NODE_ENV === 'development') {
  setVerbosity('debug');
  loadDevMessages();
  loadErrorMessages();
}

export function ApolloWrapper({
  children,
}: React.PropsWithChildren<{
  // this will be passed in from a RSC that can read cookies
  // on the client we want to read the cookie instead
  // but in SSR we don't have access to cookies, so
  // we have to use this weird workaround
  delay: number;
}>) {
  return (
    <ApolloNextAppProvider makeClient={makeClient}>
      {children}
    </ApolloNextAppProvider>
  );
}

function makeClient() {
  const httpLink = new HttpLink({
    uri: env.NEXT_PUBLIC_GRAPHQL_API_ENDPOINT,
    credentials: 'same-origin',
  });

  const wsLink = new GraphQLWsLink(
    createClient({
      url: env.NEXT_PUBLIC_GRAPHQL_WS_API_ENDPOINT,
      connectionParams: {
        credential: 'same-origin',
      },
    }),
  );

  const retryLink = new RetryLink({
    attempts: (attempt, operation) =>
      attempt <
      (operation.getContext().maxAttempts ??
        env.NEXT_PUBLIC_APOLLO_MAX_ATTEMPTS),
  });

  const delayLink = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => {
      return {
        headers: {
          ...headers,
        },
      };
    });

    return forward(operation);
  });

  const splitLink = ApolloLink.split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      );
    },
    wsLink,
    httpLink,
  );

  const link =
    typeof window === 'undefined'
      ? ApolloLink.from([
          new SSRMultipartLink({
            stripDefer: false,
            cutoffDelay: 100,
          }),
          delayLink,
          retryLink,
          splitLink,
        ])
      : ApolloLink.from([delayLink, retryLink, splitLink]);

  return new ApolloClient({
    cache: new InMemoryCache({ typePolicies }),
    link,
  });
}

const wsUrl = new URL(getRestApiUrl());
function isWsReq(url: string) {
  // Is the url for the api? - We check by running the url through an a element (nice...)
  const urlParser = new URL(url);
  const urlHost = urlParser.host;
  const urlPath = urlParser.pathname;
  const wsHost = wsUrl.host;
  const wsPath = wsUrl.pathname;
  return urlHost === wsHost && urlPath.indexOf(wsPath) === 0;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type GetConstructorArgs<T> = T extends new (...args: infer U) => any
  ? U
  : never;

export const authedFetch = async (
  ...params: GetConstructorArgs<typeof Request>
) => {
  const request = new Request(...params);

  if (!isWsReq(request.url)) {
    return fetch(request);
  }

  return fetch(request, { ...params, credentials: 'same-origin' });
};
